/**
 * Detection of devices whose OS converts stylus handwriting inside a text
 * field, without any work from us:
 * - iPadOS: Scribble writes into the focused field with the Apple Pencil,
 *   without opening the keyboard;
 * - Samsung: S Pen handwriting / direct pen input does the same.
 *
 * There is no web API to ask "does this device convert handwriting?", nor
 * one to invoke the system recognizer directly: it only ever triggers on a
 * real editable field. So the stylus-keyboard tool is offered on the
 * devices we know support it, and hidden everywhere else.
 *
 * Detecting Samsung takes two steps. Chrome on Android stopped putting the
 * device model in the user agent (since Chrome 110 it says "Android 10; K"
 * for every phone), so the model is only readable through high-entropy
 * client hints — an async call. The synchronous check still catches iPad
 * and Samsung Internet, and the async one settles the Chrome/Android case.
 */

/** Manual override for testing on a machine that is not one of those. */
const OVERRIDE_KEY = "dr:stylus-keyboard-force";

/** Samsung model codes: SM-S928B, SM-X900, GT-N7100… */
const SAMSUNG_MODEL = /\b(SM|GT|SCV?)-[A-Z]?\d{3}/i;

interface UserAgentData {
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>;
}

function hasTouchScreen(): boolean {
  return (navigator.maxTouchPoints ?? 0) > 0;
}

function isForced(): boolean {
  try {
    return localStorage.getItem(OVERRIDE_KEY) === "1";
  } catch {
    // Private mode / storage disabled: fall through to detection.
    return false;
  }
}

function isIpad(userAgent: string, touchPoints: number): boolean {
  // iPadOS 13+ reports itself as a Mac: touch points tell them apart.
  return (
    /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)
  );
}

/**
 * Best-effort answer available immediately: enough for iPad and for
 * Samsung Internet (whose user agent still carries the model).
 */
export function supportsStylusHandwriting(): boolean {
  if (typeof navigator === "undefined") return false;
  if (isForced()) return true;
  if (!hasTouchScreen()) return false;

  const userAgent = navigator.userAgent;
  return (
    isIpad(userAgent, navigator.maxTouchPoints ?? 0) ||
    /SamsungBrowser/i.test(userAgent) ||
    SAMSUNG_MODEL.test(userAgent)
  );
}

/**
 * Final answer, asking the browser for the device model when the user
 * agent hides it (Chrome on Android). Resolves to the synchronous answer
 * whenever that is already true, or when client hints are unavailable.
 */
export async function resolveStylusHandwritingSupport(): Promise<boolean> {
  if (supportsStylusHandwriting()) return true;
  if (typeof navigator === "undefined" || !hasTouchScreen()) return false;

  const uaData = (navigator as Navigator & { userAgentData?: UserAgentData })
    .userAgentData;
  if (!uaData?.getHighEntropyValues) return false;

  try {
    const { model } = await uaData.getHighEntropyValues(["model"]);
    return SAMSUNG_MODEL.test(model ?? "");
  } catch {
    // Hints refused or unsupported: stay with the synchronous answer.
    return false;
  }
}
